/*! JointJS+ v4.0.1 - HTML5 Diagramming Framework - TRIAL VERSION

Copyright (c) 2024 client IO

 2024-10-28


This Source Code Form is subject to the terms of the JointJS+ Trial License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import * as joint from '@joint/plus';
import {DirectedGraph} from '@joint/layout-directed-graph';
import {StencilService} from './stencil-service';
import {ToolbarService} from './toolbar-service';
import {InspectorService} from './inspector-service';
import {HaloService} from './halo-service';
import {KeyboardService} from './keyboard-service';
import * as appShapes from '../../shapes/app-shapes';
import {V} from "@joint/core";
import { util } from '@joint/plus';
class LogigrammeService {

  el: HTMLElement;

  graph: joint.dia.Graph;
  paper: joint.dia.Paper;
  paperScroller: joint.ui.PaperScroller;
  freeTransform: joint.ui.FreeTransform;

  commandManager: joint.dia.CommandManager;
  snaplines: joint.ui.Snaplines;
  clipboard: joint.ui.Clipboard;
  selection: joint.ui.Selection;
  navigator: joint.ui.Navigator;

  stencilService: StencilService;
  toolbarService: ToolbarService;
  inspectorService: InspectorService;
  haloService: HaloService;
  keyboardService: KeyboardService;

  readonly = false;

  widthLinks: any = [];
  heightLinks: any = [];

  constructor(
    el: HTMLElement,
    stencilService: StencilService,
    toolbarService: ToolbarService,
    inspectorService: InspectorService,
    haloService: HaloService,
    keyboardService: KeyboardService,
    readonly = false
  ) {
    this.el = el;

    // apply current joint js theme
    const view = new joint.mvc.View({el});
    view.delegateEvents({
      'mouseup input[type="range"]': (evt) => evt.target.blur()
    });

    this.stencilService = stencilService;
    this.toolbarService = toolbarService;
    this.inspectorService = inspectorService;
    this.haloService = haloService;
    this.keyboardService = keyboardService;
    this.readonly = readonly;
  }

  startRappid() {

    if (!this.readonly) {
      this.initializePaper();
      this.initializeStencil();
      this.initializeSelection();
      this.initializeToolsAndInspector();
      this.initializeNavigator();
      this.initializeToolbar();
      this.initializeKeyboardShortcuts();
      this.initializeTooltips();
    } else {
      this.initializePaper();
      this.initializeNavigator();
      this.initializeToolbar();
    }
  }

  initRichTextEditor() {
    
  }

  initializePaper() {

    const graph = this.graph = new joint.dia.Graph({}, {
      cellNamespace: appShapes
    });

    this.commandManager = new joint.dia.CommandManager({graph: graph});

    const paperWidth = 801;
    // Le 951 au lieu de 1000, correspond au "margin-top :85px" et "margin-bottom :80px" qu'on ajoute sur chaque page du pdf généré (ProcédureService : méthode preparePdf())
    // Garder à l'ésprit que si on change une, alors il faudra changer l'autre.
    const paperHeight = 951;

    const paper = this.paper = new joint.dia.Paper({
      gridSize: 1,// ceci permet de faire bouger les éléments du diagramme 1px par 1px.
      drawGridSize: 25, //ceci permet de définir la taille de notre grille.
      drawGrid: {
        name: 'mesh', // le type 'mesh' c'est pour avoir une grille avec des lignes verticales et horizontales
        args: {
          color: '#E0E0E0',
        }
      },

      width: paperWidth,
      height: paperHeight,
      model: graph,
      cellViewNamespace: appShapes,
      defaultLink: <joint.dia.Link>new appShapes.app.Link(),
      defaultConnectionPoint: appShapes.app.Link.connectionPoint,
      snapLinks: {radius: 10},
      interactive: {linkMove: false, elementMove: !this.readonly},
      async: true,
      infinitePaper: false,
      infinite: false,
      sorting: joint.dia.Paper.sorting.APPROX,
      restrictTranslate: (elementView, x, y) => {

        let defaultX = 5;
        if(elementView?.model.get('isBlocQuestion')){
          defaultX = 52;
        }
        // on restreint le translate juste à la verticale (d'où le y + 9999, qui sera la position de l'élément + une marge assez grande) sinon le width aura toujours le paper width en horizontale
        // On laisse une marge de 5 à droite et à gauche d'où le x :5 et le width : (paperWidth - 10)
        return { x: defaultX, y: 5, width: paperWidth - 2*defaultX, height: y + 9999 };
      }
    });

    paper.on('blank:contextmenu', (evt) => {
      this.renderContextToolbar({x: evt.clientX!, y: evt.clientY!});
    });

    paper.on('cell:contextmenu', (cellView, evt) => {
      this.renderContextToolbar({x: evt.clientX!, y: evt.clientY!}, [cellView.model]);
    });

    if(!this.readonly) {

      const textStyle: any =  new joint.ui.SelectButtonGroup({
        multi: true,
        selected: [],
        options: [
          { value: 'underline', content: '<span style="text-decoration: underline">U</span>' },
          { value: 'italic', content: '<span style="font-style: italic">I</span>' },
          { value: 'bold', content: '<span style="font-weight: bold">B</span>' }
        ]
      });
      const defaultAnnotation = {
        attrs: {
          'fontWeight': 'normal',
          'text-decoration': 'none',
          'font-style': 'normal',
        }
      };

      const defaultBoldAnnotation = {
        attrs: {
          'fontWeight': 'bold',
          'text-decoration': 'none',
          'font-style': 'normal',
        }
      };

      // Au doubleClick, nous donnons la possibilité à l'utilisateur de faire du texte riche sur l'élément
    paper.on('cell:pointerdblclick', (cellView, evt) => {
     const textTag = cellView.el.querySelector('text');
     if(textTag) {
       const editor = joint.ui.TextEditor.edit(textTag, {
         annotateUrls: true,
         cellView: cellView,
         annotationsProperty: 'attrs/label/annotations',
         textProperty: 'attrs/label/text',
       });
       if (editor) {

         let chosenDefaultAnnotation = {};

         if(cellView.model.get('hasDefaultBoldText')) {
           chosenDefaultAnnotation = defaultBoldAnnotation
         } else {
           chosenDefaultAnnotation = defaultAnnotation
         }

         editor.on('caret:change', () => {
           this.updateToolbar(chosenDefaultAnnotation, textStyle);
         });
         editor.on('select:changed', () => {
           this.updateToolbar(chosenDefaultAnnotation, textStyle);
         });

         const groupElement = this.toolbarService.toolbar.el.querySelector('[data-group="undo-redo-clear"]');
         if(groupElement) {
           groupElement.appendChild(textStyle.render().el)
         }
       }
     }
    })

    paper.on('blank:pointerdown', function(evt) {
      joint.ui.TextEditor.close();
    });

    textStyle.on('option:select', function(selection: any, index: any, opt: any) {
      if (opt && opt.ui) {
        const _textStyle = textStyle.getSelectionValue();
        let attrs: any = Object.assign({}, defaultAnnotation.attrs);
        if (_textStyle && _textStyle.includes('bold')) {
          attrs['fontWeight'] = 'bold';
        } else if (textStyle.fontWeightUndefined)  {
          attrs = util.omit(attrs, 'fontWeight');
        }
        if (_textStyle && _textStyle.includes('italic')) {
          attrs['font-style'] = 'italic';
        } else if (textStyle.fontStyleUndefined)  {
          attrs = util.omit(attrs, 'font-style');
        }
        if (_textStyle && _textStyle.includes('underline')) {
          attrs['text-decoration'] = 'underline';
        } else if (textStyle.textDecorationUndefined) {
          attrs = util.omit(attrs, 'text-decoration');
        }

        const selectionLength = joint.ui.TextEditor.getSelectionLength();
        const range = joint.ui.TextEditor.getSelectionRange();
        if (selectionLength > 0) {
          const newAnnotation = {
            start: range.start,
            end: range.end,
            attrs: attrs
          };
          const annotations = joint.ui.TextEditor.getAnnotations() || [];
          // @ts-ignore
          joint.ui.TextEditor.applyAnnotations(annotations.concat(newAnnotation));
        } else {
          joint.ui.TextEditor.setCurrentAnnotation(attrs);
        }
      }
      // @ts-ignore
      joint.ui.TextEditor.setCaret({ silent: true });
    });
    }

    // On calcul le centre le quart et le trois-quart de notre paper (du format A4)
    const centerX = paperWidth / 2;
    const quarterX: number = paperWidth / 4;
    const threeQuartersX: number = ( 3* paperWidth) / 4;

    this.snaplines = new joint.ui.Snaplines({paper: paper, additionalSnapPoints: function() {
        return [
          // On ajoute une ligne appelée "Snap line" custom, pour montrer le milieu, le quart et le trois-quart du paper.
          {x: centerX, y: 0},{x: quarterX, y: 0},{x: threeQuartersX, y: 0}
        ];
      }});

    const paperScroller = this.paperScroller = new joint.ui.PaperScroller({
      paper,
      autoResizePaper: true,
      scrollWhileDragging: true,
      cursor: this.readonly ? 'auto' : 'grab',
      padding: {left: 70, top: 70, right: 70, bottom: 70}
    });

    this.renderPlugin('.paper-container', paperScroller);
    paperScroller.render().center();

    paper.on('paper:pan', (evt, tx, ty) => {
      evt.preventDefault();
      paperScroller.el.scrollLeft += tx;
      paperScroller.el.scrollTop += ty;
    });

    paper.on('paper:pinch', (_evt, ox, oy, scale) => {
      // the default is already prevented
      const zoom = paperScroller.zoom();
      paperScroller.zoom(zoom * scale, {min: 0.2, max: 5, ox, oy, absolute: true});
    });

    // Pour povoir changer le link de l'element "Renvoi"
    paper.on('link:pointerdown', (linkView) => {
      const link = linkView?.model;
      const source = link?.get('source');

      if(source) {
        const sourceElement = graph.getCell(source.id);
        // si le depart du Link est bien l'élément renvoi
        if(sourceElement.attributes['type'] == 'app.RectangularRenvoiModel') {
          // Apply 'normal' router for a straight line
          link.router('orthogonal');
          // Dans ce cas on met le link en dotted sans fleche de fin
          link.attr({
            line: {
              strokeWidth: 2,
              strokeDasharray: '5,5',
              targetMarker: {
                fill: 'none', // pour enlever la fléche
                stroke: 'none'
              }
            }
          });
        }
      }

    });

    const colors = {
      red: "#ed2637",
      white: "#dde6e9",
      gray: "#cad8e3",
      darkgray: "#888888"
    };

    const pageBreaksOptions = {
      color: colors.darkgray,
      width: paperWidth,
      height: paperHeight
    };

    this.addPageBreaks(paper, pageBreaksOptions);
  }

  updateToolbar(defaultAnnotation: any, textStyle: any) {
    const attrs = joint.ui.TextEditor.getSelectionAttrs([defaultAnnotation].concat(joint.ui.TextEditor.findAnnotationsInSelection()));
    textStyle.deselect();
    if(attrs) {
      if (attrs['fontWeight'] === undefined) {
        textStyle.fontWeightUndefined = true;
      }
      if (attrs['text-decoration'] === undefined) {
        textStyle.textDecorationUndefined = true;
      }
      if (attrs['font-style'] === undefined) {
        textStyle.fontStyleUndefined = true;

      } else {
        if (attrs['fontWeight'] === 'bold') textStyle.selectByValue('bold');
        if (attrs['text-decoration'] === 'underline') textStyle.selectByValue('underline');
        if (attrs['font-style'] === 'italic') textStyle.selectByValue('italic');
      }
    }
  }

  autosize(element: any) {
    let minHeight = element.get('minHeight');
    const view = this.paper.findViewByModel(element);
    const textEl = view.findNode('label');
    const currentSize = element.size();
    // @ts-ignore
    const bbox = view.getNodeBoundingRect(textEl).inflate(20);
    let heightToApply;
    if(minHeight && (minHeight + 15) > bbox.height) {
      heightToApply = minHeight;
    } else {
      heightToApply = bbox.height;
    }

    element.resize(currentSize.width, heightToApply);
  }

  addPageBreaks(paper: any, options: any) {
    const { color, width, height } = options;

    const pageBreaksVEl = V("path", {
      stroke: color,
      fill: "none",
      "stroke-dasharray": "5,5"
    });

    paper.layers.prepend(pageBreaksVEl.node);

    let lastArea: any = null;

    function updatePageBreaks() {
      const area = paper.getArea();
      // Do not update if the area is the same
      if (area.equals(lastArea)) return;
      lastArea = area;
      let d = "";
      // Draw vertical lines
      // Do not draw the first and last lines
      for (let x = width; x < area.width; x += width) {
        d += `M ${area.x + x} ${area.y} v ${area.height}`;
      }
      // Draw horizontal lines
      // Do not draw the first and last lines
      for (let y = height; y < area.height; y += height) {
        d += ` M ${area.x} ${area.y + y} h ${area.width}`;
      }
      pageBreaksVEl.attr("d", d || null);
    }

    updatePageBreaks();

    paper.on("translate resize", updatePageBreaks);

    return () => {
      paper.off("translate resize", updatePageBreaks);
      pageBreaksVEl.remove();
    };
  }

  initializeStencil() {

    const {stencilService, paperScroller, snaplines} = this;
    stencilService.create(paperScroller, snaplines);

    this.renderPlugin('.stencil-container', stencilService.stencil);
    stencilService.setShapes();

    stencilService.stencil.on('element:drag', (cloneView, evt, dropArea, validDropTarget) => {
      cloneView.vel.attr('opacity', 0.5);
    });

    stencilService.stencil.on('element:drop', (elementView: joint.dia.ElementView) => {
      this.selection.collection.reset([elementView.model]);

      // Si c'est l'élément du titre, on le centre en haut de page
      if(elementView.model.get('isTitreProcedure')) {
          // On le rend non draggable
          elementView.setInteractivity({elementMove: false});

          // On récupère le width du paper
          const paperWidth: any = this.paper.options.width;
          // On récupère le width de l'élément.
          const shapeWidth = elementView.model.get('size')?.width;

          if( shapeWidth  && paperWidth ) {
            // On calcule le center par rapport à l'axe des x, puis on centre l'élément sur l'axe des x.
            const centerX = (paperWidth - shapeWidth) / 2;
            elementView.model.position(centerX, 50);
          }
        }
    })
  }

  initializeSelection() {

    this.clipboard = new joint.ui.Clipboard();
    this.selection = new joint.ui.Selection({
      paper: this.paperScroller,
      useModelGeometry: true,
      translateConnectedLinks: joint.ui.Selection.ConnectedLinksTranslation.SUBGRAPH
    });
    this.selection.collection.on('reset add remove', this.onSelectionChange.bind(this));

    const keyboard = this.keyboardService.keyboard;

    // Initiate selecting when the user grabs the blank area of the paper while the Shift key is pressed.
    // Otherwise, initiate paper pan.
    this.paper.on('blank:pointerdown', (evt: joint.dia.Event, _x: number, _y: number) => {

      if (keyboard.isActive('shift', evt)) {
        this.selection.startSelecting(evt);
      } else {
        this.selection.collection.reset([]);
        this.paperScroller.startPanning(evt);
        this.paper.removeTools();
      }
    });

    // Initiate selecting when the user grabs a cell while shift is pressed.
    this.paper.on('cell:pointerdown element:magnet:pointerdown', (cellView: joint.dia.CellView, evt: joint.dia.Event) => {

      if (keyboard.isActive('shift', evt)) {
        cellView.preventDefaultInteraction(evt);
        this.selection.startSelecting(evt);
      }

    });

    this.paper.on('element:pointerdown', (elementView: joint.dia.ElementView, evt: joint.dia.Event) => {

      // Select an element if CTRL/Meta key is pressed while the element is clicked.
      if (keyboard.isActive('ctrl meta', evt)) {
        this.selection.collection.add(elementView.model);
      }

    });

    this.paper.on('element:pointerup blank:pointerup element:mouseenter blank:mouseover', () => {
      this.graph.removeCells(this.heightLinks);
      this.graph.removeCells(this.widthLinks);
    });

    this.paper.on('blank:pointerup', () => {
      this.graph.removeCells(this.heightLinks);
      this.graph.removeCells(this.widthLinks);
    });


    // On essaye de trouver les avec le meme width et le meme height du paper
    this.paper.on('element:pointerdown', (elementView: joint.dia.ElementView) => {
      const elementWidth = elementView.model.get('size')?.width;
      const elementHeight = elementView.model.get('size')?.height;

      // PAS UTILISÉ POUR LE MOMENT, MAIS POURRA ÊTRE UTILE APRES
      /*
        const elementCenter = {
          x: (elementView.model.getBBox().x + elementView.model.getBBox().width) / 2,
          y: (elementView.model.getBBox().y + elementView.model.getBBox().height) / 2,
        };
       */

      const allCells = this.graph.getCells();

      // de l'ensemble des cells, on cherche les elements qui matchent le width et le height de l'élément sélectionné.
      const matchingWidthElements = allCells.filter(e => {
        if(e.attributes['size']) {
          const eWidth = e.attributes['size'].width;
          return (eWidth === elementWidth);
        } else {
          return false;
        }
      });
      const matchingHeightElements = allCells.filter(e => {
        if(e.attributes['size']) {
          const eHeight = e.attributes['size'].height;
          return (eHeight === elementHeight);
        } else {
          return false;
        }
      });

      // On boucle sur l'ensemble des éléments qui match le height (on fera de même pour le width)
      matchingHeightElements.forEach(matchingHeightElement => {
        const matchingHeightElementView = this.paper.findViewByModel(matchingHeightElement);

        // on prend tous les éléments, sauf celui qui est sélectionné
        if(elementView != matchingHeightElementView) {
          // on récupère le bounding box (position + size)
          const bbox = matchingHeightElementView.model.getBBox();

          // PAS UTILISÉ POUR LE MOMENT, MAIS POURRA ÊTRE UTILE APRES
          /*
          const matchingElementCenter = {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
          };
          */
            // On init un lien (trait avec des fléches aux bouts) pour marquer les éléments qui "match"
            const link = new joint.shapes.standard.Link({
              source: { x: bbox.x + bbox.width + 10, y: bbox.y  }, // Ligne en dessous de l'élément
              target: { x: bbox.x + bbox.width + 10, y: bbox.y + bbox.height  }, // Fin de la ligne
              attrs: {
                line: {
                  stroke: '#66BB6A',
                  strokeWidth: 1,
                  // On ajoute une autre fléche au bout du link (une seule par default).
                  sourceMarker: {
                    type: 'path',
                    d: 'M 10 -5 0 0 10 5 Z',
                    fill: '#66BB6A',
                  },
                },
              },
            });
            // On ajoute la ligne au graph
            this.graph.addCell(link);
            // On stock, l'ensemble des lignes dans une variable, pour les enlever une fois la sélection terminée
          this.heightLinks.push(link);
          }
        })

      // On boucle sur l'ensemble des éléments qui match le width.
      matchingWidthElements.forEach(matchingWidthElement => {
        const matchingWidthElementView = this.paper.findViewByModel(matchingWidthElement);

        // on prend tous les éléments, sauf celui qui est sélectionné
        if(elementView != matchingWidthElementView) {
          // on récupère le bounding box (position + size)
          const bbox = matchingWidthElementView.model.getBBox();

          // PAS UTILISÉ POUR LE MOMENT, MAIS POURRA ÊTRE UTILE APRES
          /*
          const matchingElementCenter = {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
          };
          */

          // On init un lien (trait avec des fléches aux bouts) pour marquer les éléments qui "match"
          const link = new joint.shapes.standard.Link({
            name: "width_indicator",
            source: { x: bbox.x, y: bbox.y + bbox.height + 10 }, // Line starts at the bottom of the element
            target: { x: bbox.x + bbox.width, y: bbox.y + bbox.height + 10 }, // Line ends at the bottom right of the element
            attrs: {
              line: {
                stroke: '#66BB6A',
                strokeWidth: 1,
                // On ajoute une autre fléche au bout du link (une seule par default).
                sourceMarker: {
                  type: 'path',
                  d: 'M 10 -5 0 0 10 5 Z',
                  fill: '#66BB6A',
                },
              },
            },
          });
          // On ajoute la ligne au graph
          this.graph.addCell(link);
          // On stock, l'ensemble des lignes dans une variable, pour les enlever une fois la sélection terminée
          this.widthLinks.push(link);
        }
      })
    })

    this.graph.on('remove', (cell: joint.dia.Cell) => {

      // If element is removed from the graph, remove from the selection too.
      if (this.selection.collection.has(cell)) {
        this.selection.collection.reset(this.selection.collection.models.filter(c => c !== cell));
      }

    });

    this.selection.on('selection-box:pointerdown', (elementView: joint.dia.ElementView, evt: joint.dia.Event) => {

      // Unselect an element if the CTRL/Meta key is pressed while a selected element is clicked.
      if (keyboard.isActive('ctrl meta', evt)) {
        this.selection.collection.remove(elementView.model);
      }

    }, this);

    this.selection.on('selection-box:pointerup', (elementView: joint.dia.ElementView, evt: joint.dia.Event) => {

      if (evt.button === 2) {
        evt.stopPropagation();
        this.renderContextToolbar({x: evt.clientX!, y: evt.clientY!}, this.selection.collection.toArray());
      }

    }, this);
  }

  renderContextToolbar(point: joint.dia.Point, cellsToCopy: joint.dia.Cell[] = []) {
    this.selection.collection.reset(cellsToCopy);
    const contextToolbar = new joint.ui.ContextToolbar({
      target: point,
      root: this.paper.el,
      padding: 0,
      vertical: true,
      anchor: 'top-left',
      tools: [
        {
          action: 'copy',
          content: 'Copy',
          attrs: {
            'disabled': cellsToCopy.length === 0
          }
        },
        {
          action: 'paste',
          content: 'Paste',
          attrs: {
            'disabled': this.clipboard.isEmpty()
          }
        }]
    });

    contextToolbar.on('action:copy', () => {
      contextToolbar.remove();

      this.clipboard.copyElements(cellsToCopy, this.graph);
    });

    contextToolbar.on('action:paste', () => {
      contextToolbar.remove();
      const pastedCells = this.clipboard.pasteCellsAtPoint(this.graph, this.paper.clientToLocalPoint(point));

      const elements = pastedCells.filter(cell => cell.isElement());

      // Make sure pasted elements get selected immediately. This makes the UX better as
      // the user can immediately manipulate the pasted elements.
      this.selection.collection.reset(elements);
    });
    contextToolbar.render();
  }

  onSelectionChange() {
    const {paper, selection} = this;
    const {collection} = selection;
    paper.removeTools();
    joint.ui.Halo.clear(paper);
    joint.ui.FreeTransform.clear(paper);
    joint.ui.Inspector.close();
    if (collection.length === 1) {
      const primaryCell: joint.dia.Cell = collection.first();
      const primaryCellView = paper.findViewByModel(primaryCell);
      selection.destroySelectionBox(primaryCell);
      this.selectPrimaryCell(primaryCellView);
    } else if (collection.length === 2) {
      collection.each(function (cell: joint.dia.Cell) {
        selection.createSelectionBox(cell);
      });
    }
  }

  selectPrimaryCell(cellView: joint.dia.CellView) {
    const cell = cellView.model;
    const allCells = this.paper.model.getCells();
    if (cell.isElement()) {
      this.selectPrimaryElement(<joint.dia.ElementView>cellView);
    } else {
      this.selectPrimaryLink(<joint.dia.LinkView>cellView);
    }
    this.inspectorService.create(cell, allCells);
  }

  selectPrimaryElement(elementView: joint.dia.ElementView) {
    const element = elementView.model;

    this.freeTransform = new joint.ui.FreeTransform({
      cellView: elementView,
      allowRotation: false,
      preserveAspectRatio: !!element.get('preserveAspectRatio'),
      allowOrthogonalResize: element.get('allowOrthogonalResize') !== false
    }).render();

    this.haloService.create(elementView);

    element.on('change:size', (elementViewChanging: joint.dia.ElementView) => {
      this.graph.removeCells(this.heightLinks);
      this.graph.removeCells(this.widthLinks);
      const elementWidth = elementViewChanging.attributes['size'].width;
      const elementHeight = elementViewChanging.attributes['size'].height;
      const allCells = this.graph.getCells();

      const matchingHeightElements = allCells.filter(e => {
        if(e.attributes['size']) {
          const eHeight = e.attributes['size'].height;
          // You can change the condition based on whether you want to match width, height or both
          return (eHeight === elementHeight);
        } else {
          return false;
        }
      });
      // On boucle sur l'ensemble des éléments qui match le height (on fera de même pour le width)
      matchingHeightElements.forEach(matchingHeightElement => {
        const matchingHeightElementView = this.paper.findViewByModel(matchingHeightElement);

        // on prend tous les éléments, sauf celui qui est sélectionné
        if(elementView != matchingHeightElementView) {
          // on récupère le bounding box (position + size)
          const bbox = matchingHeightElementView.model.getBBox();

          // PAS UTILISÉ POUR LE MOMENT, MAIS POURRA ÊTRE UTILE APRES
          /*
          const matchingElementCenter = {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
          };
          */
          // On init un lien (trait avec des fléches aux bouts) pour marquer les éléments qui "match"
          const link = new joint.shapes.standard.Link({
            source: { x: bbox.x + bbox.width + 10, y: bbox.y  }, // Ligne en dessous de l'élément
            target: { x: bbox.x + bbox.width + 10, y: bbox.y + bbox.height  }, // Fin de la ligne
            attrs: {
              line: {
                stroke: '#66BB6A',
                strokeWidth: 1,
                // On ajoute une autre fléche au bout du link (une seule par default).
                sourceMarker: {
                  type: 'path',
                  d: 'M 10 -5 0 0 10 5 Z',
                  fill: '#66BB6A',
                },
              },
            },
          });
          // On ajoute la ligne au graph
          this.graph.addCell(link);
          // On stock, l'ensemble des lignes dans une variable, pour les enlever une fois la sélection terminée
          this.heightLinks.push(link);
        }
      })

      // de l'ensemble des cells, on cherche les elements qui matchent le width et le height de l'élément sélectionné.
      const matchingWidthElements = allCells.filter(e => {
        if(e.attributes['size']) {
          const eWidth = e.attributes['size'].width;
          // You can change the condition based on whether you want to match width, height or both
          return (eWidth === elementWidth);
        } else {
          return false;
        }
      });
      matchingWidthElements.forEach(matchingWidthElement => {

        const matchingWidthElementView = this.paper.findViewByModel(matchingWidthElement);

        // on prend tous les éléments, sauf celui qui est sélectionné
        if(elementView != matchingWidthElementView) {
          // on récupère le bounding box (position + size)
          const bbox = matchingWidthElementView.model.getBBox();

          // PAS UTILISÉ POUR LE MOMENT, MAIS POURRA ÊTRE UTILE APRES
          /*
          const matchingElementCenter = {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
          };
          */

          // On init un lien (trait avec des fléches aux bouts) pour marquer les éléments qui "match"
          const link = new joint.shapes.standard.Link({
            name: "width_indicator",
            source: { x: bbox.x, y: bbox.y + bbox.height + 10 }, // Line starts at the bottom of the element
            target: { x: bbox.x + bbox.width, y: bbox.y + bbox.height + 10 }, // Line ends at the bottom right of the element
            attrs: {
              line: {
                stroke: '#66BB6A',
                strokeWidth: 1,
                // On ajoute une autre fléche au bout du link (une seule par default).
                sourceMarker: {
                  type: 'path',
                  d: 'M 10 -5 0 0 10 5 Z',
                  fill: '#66BB6A',
                },
              },
            },
          });
          // On ajoute la ligne au graph
          this.graph.addCell(link);
          // On stock, l'ensemble des lignes dans une variable, pour les enlever une fois la sélection terminée
          this.widthLinks.push(link);
        }
      })

    })
    this.freeTransform.on('resize:stop', () => {
      this.graph.removeCells(this.heightLinks);
      this.graph.removeCells(this.widthLinks);
    });
  }

  selectPrimaryLink(linkView: joint.dia.LinkView) {

    const ns = joint.linkTools;
    const toolsView = new joint.dia.ToolsView({
      name: 'link-pointerdown',
      tools: [
        new ns.Vertices(),
        new ns.SourceAnchor(),
        new ns.TargetAnchor(),
        new ns.SourceArrowhead(),
        new ns.TargetArrowhead(),
        new ns.Segments,
        new ns.Boundary({padding: 15}),
        new ns.Remove({offset: -20, distance: 40})
      ]
    });

    linkView.addTools(toolsView);
  }

  initializeToolsAndInspector() {

    this.paper.on('cell:pointerup', (cellView: joint.dia.CellView) => {
      const cell = cellView.model;
      const {collection} = this.selection;
      if (collection.includes(cell)) {
        return;
      }
      collection.reset([cell]);
    });

    this.paper.on('link:mouseenter', (linkView: joint.dia.LinkView) => {

      // Open tool only if there is none yet
      if (linkView.hasTools()) {
        return;
      }

      const ns = joint.linkTools;
      const toolsView = new joint.dia.ToolsView({
        name: 'link-hover',
        tools: [
          new ns.Vertices({vertexAdding: false}),
          new ns.SourceArrowhead(),
          new ns.TargetArrowhead()
        ]
      });

      linkView.addTools(toolsView);
    });

    this.paper.on('link:mouseleave', (linkView: joint.dia.LinkView) => {

      // Remove only the hover tool, not the pointerdown tool
      if (linkView.hasTools('link-hover')) {
        linkView.removeTools();
      }
    });

    this.graph.on('change', (cell: joint.dia.Cell, opt: any) => {

      if (!cell.isLink() || !opt.inspector) {
        return;
      }

      const ns = joint.linkTools;
      const toolsView = new joint.dia.ToolsView({
        name: 'link-inspected',
        tools: [
          new ns.Boundary({padding: 15}),
        ]
      });

      cell.findView(this.paper).addTools(toolsView);

    });

    this.graph.on('change', (cell: joint.dia.Cell) => {
      if (cell.hasChanged('attrs')) this.autosize(cell);
    });
  }

  initializeNavigator() {

  const navigator = this.navigator = new joint.ui.Navigator({
    width: 240,
    height: 115,
    paperScroller: this.paperScroller,
    zoom: false,
    paperOptions: {
      async: true,
      sorting: joint.dia.Paper.sorting.NONE,
      elementView: appShapes.NavigatorElementView,
      linkView: appShapes.NavigatorLinkView,
      cellViewNamespace: { /* no other views are accessible in the navigator */}
    }
  });
  this.renderPlugin('.navigator-container', navigator);
  }

  initializeToolbar() {
    const backgroundColor = "#F3F7F6";

    const exportOptions = {
      padding: 20,
      useComputedStyles: false,
      backgroundColor,
      size: "2x"
    };

    this.toolbarService.create(this.commandManager, this.paperScroller, this.readonly);

    /*
    this.toolbarService.toolbar.on({
      'svg:pointerclick': this.openAsSVG.bind(this),
      'png:pointerclick': this.openAsPNG.bind(this),
      'to-front:pointerclick': this.applyOnSelection.bind(this, 'toFront'),
      'to-back:pointerclick': this.applyOnSelection.bind(this, 'toBack'),
      'layout:pointerclick': this.layoutDirectedGraph.bind(this),
      'snapline:change': this.changeSnapLines.bind(this),
      'clear:pointerclick': this.graph.clear.bind(this.graph),
      'print:pointerclick': () => joint.format.print(this.paper, {grid: true}),
      'grid-size:change': this.paper.setGridSize.bind(this.paper)
    });
    */

    this.renderPlugin('.toolbar-container', this.toolbarService.toolbar);

    // on ajoute les événements liés aux button du toolbar
    this.toolbarService.toolbar.on('undo:pointerclick', () => {
      this.commandManager.undo();
    });

    this.toolbarService.toolbar.on('redo:pointerclick', () => {
      this.commandManager.redo();
    });

    this.toolbarService.toolbar.on('clear:pointerclick', () => {
      this.graph.clear();
    });
  }

  applyOnSelection(method: string) {
    this.graph.startBatch('selection');
    this.selection.collection.models.forEach(function (model: joint.dia.Cell) {
      (model as any)[method]();
    });
    this.graph.stopBatch('selection');
  }

  changeSnapLines(checked: boolean) {

    if (checked) {
      this.snaplines.enable();
    } else {
      this.snaplines.disable();
    }
  }

  initializeKeyboardShortcuts() {

    this.keyboardService.create(
      this.graph, this.clipboard, this.selection, this.paperScroller, this.commandManager);
  }

  initializeTooltips(): joint.ui.Tooltip {

    return new joint.ui.Tooltip({
      rootTarget: document.body,
      target: '[data-tooltip]',
      direction: joint.ui.Tooltip.TooltipArrowPosition.Auto,
      padding: 10
    });
  }

  openAsSVG() {

    this.paper.hideTools();
    joint.format.toSVG(this.paper, (svg: string) => {
      new joint.ui.Lightbox({
        image: 'data:image/svg+xml,' + encodeURIComponent(svg),
        downloadable: true,
        fileName: 'Rappid'
      }).open();
      this.paper.showTools();
    }, {
      preserveDimensions: true,
      convertImagesToDataUris: true,
      useComputedStyles: false,
      grid: true
    });
  }

  openAsPNG() {

    this.paper.hideTools();
    joint.format.toPNG(this.paper, (dataURL: string) => {
      new joint.ui.Lightbox({
        image: dataURL,
        downloadable: true,
        fileName: 'Rappid'
      }).open();
      this.paper.showTools();
    }, {
      padding: 10,
      useComputedStyles: false,
      grid: true
    });
  }

  layoutDirectedGraph() {

    DirectedGraph.layout(this.graph, {
      setVertices: true,
      rankDir: 'TB',
      marginX: 100,
      marginY: 100
    });

    this.paperScroller.centerContent({useModelGeometry: true});
  }

  renderPlugin(selector: string, plugin: any): void {

    this.el?.querySelector(selector)!.appendChild(plugin.el);
    plugin.render();
  }
}

export default LogigrammeService;
